<template>
  <div class="call-contacts" v-loading.fullscreen.lock="loading">
    <section class="call-contacts__panel">
      <div v-if="!registeredUser" class="call-contacts__head">
        <div>
          <p>Данные сотрудника для правильного распределения номеров.</p>
        </div>

        <el-tag type="warning" effect="plain">
          Требуется регистрация
        </el-tag>
      </div>

      <el-alert
        v-if="errorMessage"
        class="call-contacts__alert"
        type="error"
        title="Ошибка"
        :description="errorMessage"
        show-icon
        :closable="false"
      />

      <el-form
        v-if="!registeredUser"
        ref="registerForm"
        class="call-contacts__form"
        :model="form"
        :rules="registrationRules"
        label-position="top"
      >
        <el-form-item label="ФИО" prop="full_name">
          <el-input v-model.trim="form.full_name" disabled />
        </el-form-item>

        <el-form-item label="Роль" prop="role">
          <el-radio-group
            v-model="form.role"
            class="call-contacts__role-group"
            @change="handleRoleChange"
          >
            <el-radio-button
              v-for="role in roleOptions"
              :key="role.value"
              :label="role.value"
            >
              {{ role.label }}
            </el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="Департамент" prop="department">
          <el-select
            v-model="form.department"
            placeholder="Выберите департамент"
            filterable
            :disabled="departmentsLoading"
            style="width: 100%"
          >
            <el-option
              v-for="department in normalizedDepartments"
              :key="department.value"
              :label="department.label"
              :value="department.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.role !== 'director'"
          label="Направление"
          prop="direction"
        >
          <el-select
            v-model="form.direction"
            placeholder="Выберите направление"
            filterable
            :disabled="directionsLoading"
            style="width: 100%"
          >
            <el-option
              v-for="direction in normalizedDirections"
              :key="direction.value"
              :label="direction.label"
              :value="direction.value"
            />
          </el-select>
        </el-form-item>

        <el-button
          type="primary"
          icon="el-icon-check"
          :loading="registerLoading"
          @click="registerUser"
        >
          Зарегистрироваться
        </el-button>
      </el-form>

      <div v-else class="call-contacts__work">
        <el-tabs
          v-if="hasWorkTabs"
          v-model="activeWorkTab"
          class="call-contacts__tabs"
          @tab-click="handleWorkTabClick"
        >
          <el-tab-pane label="Номера" name="phones" />
          <el-tab-pane label="Статистика" name="stats" />
        </el-tabs>

        <section
          v-if="showOverviewPanel"
          v-loading="overviewLoading"
          class="call-contacts__overview"
        >
          <div class="call-contacts__section-head call-contacts__section-head--overview">
            <div>
              <span class="call-contacts__eyebrow">{{ currentRoleLabel }}</span>
              <h3>Статистика менеджеров</h3>
            </div>

            <div class="call-contacts__filters call-contacts__filters--overview">
              <el-select
                v-model="overviewFilters.manager_id"
                placeholder="Все менеджеры"
                filterable
                clearable
                :disabled="overviewLoading"
                @change="handleOverviewFiltersChange"
              >
                <el-option
                  v-for="manager in overviewManagers"
                  :key="manager.id_crm"
                  :label="manager.full_name"
                  :value="manager.id_crm"
                />
              </el-select>

              <el-date-picker
                v-model="overviewFilters.dateRange"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="—"
                start-placeholder="От"
                end-placeholder="До"
                clearable
                :disabled="overviewLoading"
                @change="handleOverviewFiltersChange"
              />
            </div>
          </div>

          <div class="call-contacts__kpi-grid">
            <article
              v-for="card in overviewCards"
              :key="card.label"
              class="call-contacts__kpi-card"
            >
              <span>{{ card.label }}</span>
              <strong>{{ card.value }}</strong>
            </article>
          </div>

          <div class="call-contacts__overview-grid">
            <section class="call-contacts__chart-panel">
              <div class="call-contacts__section-head call-contacts__section-head--compact">
                <div>
                  <span class="call-contacts__eyebrow">Последние 10 дней</span>
                  <h3>Динамика звонков</h3>
                </div>
              </div>

              <div class="call-contacts__chart">
                <svg viewBox="0 0 640 220" role="img" aria-label="График звонков за последние 10 дней">
                  <line
                    v-for="line in chartGridLines"
                    :key="line"
                    x1="28"
                    x2="620"
                    :y1="line"
                    :y2="line"
                    class="call-contacts__chart-grid"
                  />
                  <polygon
                    v-if="overviewChartAreaPoints"
                    :points="overviewChartAreaPoints"
                    class="call-contacts__chart-area"
                  />
                  <polyline
                    v-if="overviewChartPoints"
                    :points="overviewChartPoints"
                    class="call-contacts__chart-line"
                  />
                  <g
                    v-for="point in overviewChartDots"
                    :key="point.date"
                  >
                    <circle
                      :cx="point.x"
                      :cy="point.y"
                      r="4"
                      class="call-contacts__chart-dot"
                    />
                    <text
                      :x="point.x"
                      :y="point.y - 10"
                      text-anchor="middle"
                      class="call-contacts__chart-value"
                    >
                      {{ point.calls }}
                    </text>
                  </g>
                </svg>

                <div class="call-contacts__chart-labels">
                  <span
                    v-for="point in overviewChartDots"
                    :key="`${point.date}-label`"
                  >
                    {{ point.label }}
                  </span>
                </div>
              </div>
            </section>

            <section class="call-contacts__managers-panel">
              <div class="call-contacts__section-head call-contacts__section-head--compact">
                <div>
                  <span class="call-contacts__eyebrow">Период</span>
                  <h3>Менеджеры</h3>
                </div>
              </div>

              <el-table
                class="call-contacts__table call-contacts__overview-table"
                :data="overviewManagerStats"
                size="small"
                empty-text="Нет данных за период"
              >
                <el-table-column prop="manager_name" label="Менеджер" min-width="190" />
                <el-table-column prop="calls" label="Звонки" width="90" align="right" />
                <el-table-column prop="processed_calls" label="Обработано" width="110" align="right" />
                <el-table-column prop="need_calls" label="Потребности" width="110" align="right" />
                <el-table-column label="Выручка" width="120" align="right">
                  <template slot-scope="{ row }">
                    {{ formatIntegerValue(row.revenue) }}
                  </template>
                </el-table-column>
                <el-table-column label="Маржа" width="120" align="right">
                  <template slot-scope="{ row }">
                    {{ formatIntegerValue(row.margin) }}
                  </template>
                </el-table-column>
              </el-table>

              <div class="call-contacts__mobile-list call-contacts__overview-mobile">
                <p v-if="!overviewManagerStats.length" class="call-contacts__empty">
                  Нет данных за период
                </p>

                <article
                  v-for="manager in overviewManagerStats"
                  :key="manager.manager_id"
                  class="call-contacts__mobile-card"
                >
                  <div class="call-contacts__mobile-card-head">
                    <strong class="call-contacts__manager-name">{{ manager.manager_name }}</strong>
                    <el-tag type="info" effect="plain" size="mini">
                      {{ manager.calls }} звонков
                    </el-tag>
                  </div>

                  <div class="call-contacts__manager-metrics">
                    <span>Обработано <strong>{{ manager.processed_calls }}</strong></span>
                    <span>Ответили <strong>{{ manager.answered_calls }}</strong></span>
                    <span>Потребности <strong>{{ manager.need_calls }}</strong></span>
                    <span>Выручка <strong>{{ formatIntegerValue(manager.revenue) }}</strong></span>
                    <span>Маржа <strong>{{ formatIntegerValue(manager.margin) }}</strong></span>
                  </div>
                </article>
              </div>
            </section>
          </div>
        </section>

        <template v-if="showPhonePanel">
        <div
          v-if="!currentPhone || isEditingAssignedPhone"
          class="call-contacts__start"
          :class="{ 'call-contacts__start--empty': noPhonesAvailable }"
        >
          <div
            v-if="noPhonesAvailable"
            class="call-contacts__queue-empty"
          >
            <span class="call-contacts__queue-icon">
              <i class="el-icon-finished" />
            </span>
            <div>
              <span class="call-contacts__eyebrow">Очередь номеров</span>
              <strong>Новых номеров нет</strong>
              <p>Свободные контакты закончились. Проверьте очередь чуть позже.</p>
            </div>
          </div>

          <div v-else>
            <span class="call-contacts__eyebrow">Очередь номеров</span>
            <p>Возьмите следующий контакт, чтобы начать обработку.</p>
          </div>

          <el-button
            class="call-contacts__next-button"
            type="primary"
            :icon="noPhonesAvailable ? 'el-icon-refresh' : 'el-icon-phone-outline'"
            :loading="phoneLoading"
            @click="getNextPhone"
          >
            {{ noPhonesAvailable ? "Проверить снова" : "Получить телефон" }}
          </el-button>
        </div>

        <section
          v-if="currentPhone && !isEditingAssignedPhone"
          class="call-contacts__phone"
        >
          <div class="call-contacts__phone-head">
            <div>
              <span class="call-contacts__caption">
                Текущий номер
              </span>
              <a :href="`tel:${currentPhone.phone}`" class="call-contacts__number">
                {{ formatPhoneNumber(currentPhone.phone) }}
              </a>
            </div>
          </div>

          <dl class="call-contacts__meta-grid">
            <div>
              <dt>Источник</dt>
              <dd>{{ currentPhone.source_name || "—" }}</dd>
            </div>
            <div>
              <dt>Телефон источника</dt>
              <dd>{{ formatPhoneNumber(currentPhone.source_phone) }}</dd>
            </div>
            <div>
              <dt>Департамент</dt>
              <dd>{{ currentPhone.department || "—" }}</dd>
            </div>
            <div>
              <dt>Направление</dt>
              <dd>{{ currentPhone.direction || "—" }}</dd>
            </div>
          </dl>

          <el-form
            ref="resultForm"
            class="call-contacts__result-form"
            :model="resultForm"
            :rules="resultRules"
            label-position="top"
          >
            <el-form-item label="Ответил" prop="answered">
              <el-radio-group
                class="call-contacts__radio-group"
                v-model="resultForm.answered"
                @change="handleAnsweredChange"
              >
                <el-radio :label="true">Да</el-radio>
                <el-radio :label="false">Нет</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="resultForm.answered === true" label="Причина" prop="reason">
              <el-select
                v-model="resultForm.reason"
                placeholder="Выберите причину"
                clearable
                style="width: 100%"
              >
                <el-option
                  v-for="reason in reasonOptions"
                  :key="reason"
                  :label="reason"
                  :value="reason"
                />
              </el-select>
            </el-form-item>

            <el-form-item v-if="resultForm.answered === true" label="Есть техника" prop="has_equipment">
              <el-radio-group class="call-contacts__radio-group" v-model="resultForm.has_equipment">
                <el-radio label="Да">Да</el-radio>
                <el-radio label="Нет">Нет</el-radio>
                <el-radio label="Не знаю">Не знаю</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="resultForm.answered === true" label="Потребность" prop="has_need">
              <el-radio-group
                class="call-contacts__radio-group"
                v-model="resultForm.has_need"
                @change="handleHasNeedChange"
              >
                <el-radio :label="true">Да</el-radio>
                <el-radio :label="false">Нет</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item
              v-if="resultForm.answered === true && resultForm.has_need === true"
              class="call-contacts__result-form-row"
              label="Направление техники"
              prop="equipment_direction"
            >
              <el-input
                v-model.trim="resultForm.equipment_direction"
                placeholder="Например, погрузчики"
              />
            </el-form-item>

            <el-form-item
              v-if="resultForm.answered === true"
              class="call-contacts__result-form-row"
              label="Комментарий"
              prop="comment"
            >
              <el-input
                v-model.trim="resultForm.comment"
                type="textarea"
                :rows="4"
                placeholder="Комментарий менеджера"
              />
            </el-form-item>

            <el-button
              class="call-contacts__submit-button"
              type="success"
              icon="el-icon-check"
              :loading="resultLoading"
              @click="submitPhoneResult"
            >
              Сохранить результат
            </el-button>
          </el-form>
        </section>

        <section class="call-contacts__assigned">
          <div class="call-contacts__section-head">
            <div>
              <span class="call-contacts__eyebrow">История обработки</span>
              <h3>Выданные номера</h3>
            </div>

            <div class="call-contacts__filters">
              <el-date-picker
                v-model="assignedFilters.date"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="Дата выдачи"
                clearable
                :disabled="assignedLoading"
                @change="handleAssignedDateChange"
              />
            </div>
          </div>

          <el-table
            v-loading="assignedLoading"
            ref="assignedTable"
            class="call-contacts__table"
            :data="assignedPhones"
            row-key="id"
            :expand-row-keys="expandedAssignedPhoneId ? [expandedAssignedPhoneId] : []"
            size="small"
            empty-text="Нет выданных номеров"
          >
            <el-table-column type="expand" width="1">
              <template slot-scope="{ row }">
                <transition name="call-contacts-slide-down" appear>
                  <section
                    v-if="isAssignedPhoneExpanded(row)"
                    class="call-contacts__phone call-contacts__phone--inline"
                  >
                    <el-button
                      class="call-contacts__collapse-button"
                      type="text"
                      size="mini"
                      icon="el-icon-arrow-up"
                      title="Свернуть"
                      @click="collapseAssignedPhone"
                    >
                      <span class="call-contacts__collapse-label">Свернуть</span>
                    </el-button>

                    <div class="call-contacts__phone-head">
                      <div>
                        <span class="call-contacts__caption">Редактирование номера</span>
                        <a :href="`tel:${currentPhone.phone}`" class="call-contacts__number">
                          {{ formatPhoneNumber(currentPhone.phone) }}
                        </a>
                      </div>

                    </div>

                    <el-form
                      ref="resultForm"
                      class="call-contacts__result-form"
                      :model="resultForm"
                      :rules="resultRules"
                      label-position="top"
                    >
                      <el-form-item label="Ответил" prop="answered">
                        <el-radio-group
                          class="call-contacts__radio-group"
                          v-model="resultForm.answered"
                          @change="handleAnsweredChange"
                        >
                          <el-radio :label="true">Да</el-radio>
                          <el-radio :label="false">Нет</el-radio>
                        </el-radio-group>
                      </el-form-item>

                      <el-form-item v-if="resultForm.answered === true" label="Причина" prop="reason">
                        <el-select
                          v-model="resultForm.reason"
                          placeholder="Выберите причину"
                          clearable
                          style="width: 100%"
                        >
                          <el-option
                            v-for="reason in reasonOptions"
                            :key="reason"
                            :label="reason"
                            :value="reason"
                          />
                        </el-select>
                      </el-form-item>

                      <el-form-item v-if="resultForm.answered === true" label="Есть техника" prop="has_equipment">
                        <el-radio-group class="call-contacts__radio-group" v-model="resultForm.has_equipment">
                          <el-radio label="Да">Да</el-radio>
                          <el-radio label="Нет">Нет</el-radio>
                          <el-radio label="Не знаю">Не знаю</el-radio>
                        </el-radio-group>
                      </el-form-item>

                      <el-form-item v-if="resultForm.answered === true" label="Потребность" prop="has_need">
                        <el-radio-group
                          class="call-contacts__radio-group"
                          v-model="resultForm.has_need"
                          @change="handleHasNeedChange"
                        >
                          <el-radio :label="true">Да</el-radio>
                          <el-radio :label="false">Нет</el-radio>
                        </el-radio-group>
                      </el-form-item>

                      <el-form-item
                        v-if="resultForm.answered === true && resultForm.has_need === true"
                        class="call-contacts__result-form-row"
                        label="Направление техники"
                        prop="equipment_direction"
                      >
                        <el-input
                          v-model.trim="resultForm.equipment_direction"
                          placeholder="Например, погрузчики"
                        />
                      </el-form-item>

                      <el-form-item
                        v-if="resultForm.answered === true"
                        class="call-contacts__result-form-row"
                        label="Комментарий"
                        prop="comment"
                      >
                        <el-input
                          v-model.trim="resultForm.comment"
                          type="textarea"
                          :rows="4"
                          placeholder="Комментарий менеджера"
                        />
                      </el-form-item>

                      <div
                        v-if="resultForm.answered === true && resultForm.has_need === true"
                        class="call-contacts__financial-fields"
                      >
                        <el-form-item label="Выручка" prop="revenue">
                          <el-input-number
                            v-model="resultForm.revenue"
                            :min="0"
                            :precision="0"
                            :step="1000"
                            controls-position="right"
                            placeholder="0"
                            style="width: 100%"
                          />
                        </el-form-item>

                        <el-form-item label="Маржа" prop="margin">
                          <el-input-number
                            v-model="resultForm.margin"
                            :min="0"
                            :precision="0"
                            :step="1000"
                            controls-position="right"
                            placeholder="0"
                            style="width: 100%"
                          />
                        </el-form-item>
                      </div>

                      <el-button
                        class="call-contacts__submit-button"
                        type="success"
                        icon="el-icon-check"
                        :loading="resultLoading"
                        @click="submitPhoneResult"
                      >
                        Сохранить результат
                      </el-button>
                    </el-form>
                  </section>
                </transition>
              </template>
            </el-table-column>
            <el-table-column label="Телефон" width="150">
              <template slot-scope="{ row }">
                <a :href="`tel:${row.phone}`" class="call-contacts__table-phone">
                  {{ formatPhoneNumber(row.phone) }}
                </a>
              </template>
            </el-table-column>
            <el-table-column label="Информация" min-width="420">
              <template slot-scope="{ row }">
                <div
                  class="call-contacts__assigned-info"
                  :class="{ 'call-contacts__assigned-info--result': hasAssignedInfoResult(row) }"
                >
                  <div
                    v-for="item in getAssignedInfoItems(row)"
                    :key="item.label"
                    class="call-contacts__assigned-info-item"
                  >
                    <span>{{ item.label }}</span>
                    <strong :title="item.value">{{ item.value }}</strong>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Дата создания" width="140">
              <template slot-scope="{ row }">
                {{ formatPhoneCreatedDate(row) }}
              </template>
            </el-table-column>
            <el-table-column label="Статус" width="130">
              <template slot-scope="{ row }">
                <el-tag
                  class="call-contacts__status-tag"
                  :type="getAssignedStatusType(row)"
                  effect="plain"
                  size="mini"
                >
                  {{ getAssignedStatusText(row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="112" align="right">
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  size="mini"
                  :loading="detailLoading && detailPhoneId === row.id"
                  @click="openAssignedPhone(row.id)"
                >
                  {{ isAssignedPhoneExpanded(row) ? "Свернуть" : "Изменить" }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div
            v-loading="assignedLoading"
            class="call-contacts__mobile-list"
          >
            <p v-if="!assignedPhones.length" class="call-contacts__empty">
              Нет выданных номеров
            </p>

            <article
              v-for="phone in assignedPhones"
              :key="phone.id"
              class="call-contacts__mobile-card"
            >
              <div class="call-contacts__mobile-card-head">
                <a :href="`tel:+${phone.phone}`" class="call-contacts__mobile-phone">
                  +{{ formatPhoneNumber(phone.phone) }}
                </a>

                <el-tag
                  class="call-contacts__status-tag"
                  :type="getAssignedStatusType(phone)"
                  effect="plain"
                  size="mini"
                >
                  {{ getAssignedStatusText(phone) }}
                </el-tag>
              </div>

              <div
                class="call-contacts__assigned-info call-contacts__assigned-info--mobile"
                :class="{ 'call-contacts__assigned-info--result': hasAssignedInfoResult(phone) }"
              >
                <div
                  v-for="item in getAssignedInfoItems(phone)"
                  :key="item.label"
                  class="call-contacts__assigned-info-item"
                >
                  <span>{{ item.label }}</span>
                  <strong :title="item.value">{{ item.value }}</strong>
                </div>
              </div>

              <dl class="call-contacts__mobile-meta">
                <div>
                  <dt>Дата создания</dt>
                  <dd>{{ formatPhoneCreatedDate(phone) }}</dd>
                </div>
              </dl>

              <el-button
                class="call-contacts__mobile-action"
                type="primary"
                plain
                size="small"
                :loading="detailLoading && detailPhoneId === phone.id"
                @click="openAssignedPhone(phone.id)"
              >
                {{ isAssignedPhoneExpanded(phone) ? "Свернуть" : "Изменить результат" }}
              </el-button>

              <transition name="call-contacts-slide-down" appear>
                <section
                  v-if="isAssignedPhoneExpanded(phone)"
                  class="call-contacts__phone call-contacts__phone--inline call-contacts__phone--mobile-inline"
                >
                  <div class="call-contacts__phone-head">
                    <div>
                      <span class="call-contacts__caption">Редактирование номера</span>
                      <a :href="`tel:${currentPhone.phone}`" class="call-contacts__number">
                        {{ formatPhoneNumber(currentPhone.phone) }}
                      </a>
                    </div>

                  </div>

                  <el-form
                    ref="resultForm"
                    class="call-contacts__result-form"
                    :model="resultForm"
                    :rules="resultRules"
                    label-position="top"
                  >
                    <el-form-item label="Ответил" prop="answered">
                      <el-radio-group
                        class="call-contacts__radio-group"
                        v-model="resultForm.answered"
                        @change="handleAnsweredChange"
                      >
                        <el-radio :label="true">Да</el-radio>
                        <el-radio :label="false">Нет</el-radio>
                      </el-radio-group>
                    </el-form-item>

                    <el-form-item v-if="resultForm.answered === true" label="Причина" prop="reason">
                      <el-select
                        v-model="resultForm.reason"
                        placeholder="Выберите причину"
                        clearable
                        style="width: 100%"
                      >
                        <el-option
                          v-for="reason in reasonOptions"
                          :key="reason"
                          :label="reason"
                          :value="reason"
                        />
                      </el-select>
                    </el-form-item>

                    <el-form-item v-if="resultForm.answered === true" label="Есть техника" prop="has_equipment">
                      <el-radio-group class="call-contacts__radio-group" v-model="resultForm.has_equipment">
                        <el-radio label="Да">Да</el-radio>
                        <el-radio label="Нет">Нет</el-radio>
                        <el-radio label="Не знаю">Не знаю</el-radio>
                      </el-radio-group>
                    </el-form-item>

                    <el-form-item v-if="resultForm.answered === true" label="Потребность" prop="has_need">
                      <el-radio-group
                        class="call-contacts__radio-group"
                        v-model="resultForm.has_need"
                        @change="handleHasNeedChange"
                      >
                        <el-radio :label="true">Да</el-radio>
                        <el-radio :label="false">Нет</el-radio>
                      </el-radio-group>
                    </el-form-item>

                    <el-form-item
                      v-if="resultForm.answered === true && resultForm.has_need === true"
                      class="call-contacts__result-form-row"
                      label="Направление техники"
                      prop="equipment_direction"
                    >
                      <el-input
                        v-model.trim="resultForm.equipment_direction"
                        placeholder="Например, погрузчики"
                      />
                    </el-form-item>

                    <el-form-item
                      v-if="resultForm.answered === true"
                      class="call-contacts__result-form-row"
                      label="Комментарий"
                      prop="comment"
                    >
                      <el-input
                        v-model.trim="resultForm.comment"
                        type="textarea"
                        :rows="4"
                        placeholder="Комментарий менеджера"
                      />
                    </el-form-item>

                    <div
                      v-if="resultForm.answered === true && resultForm.has_need === true"
                      class="call-contacts__financial-fields"
                    >
                      <el-form-item label="Выручка" prop="revenue">
                        <el-input-number
                          v-model="resultForm.revenue"
                          :min="0"
                          :precision="0"
                          :step="1000"
                          controls-position="right"
                          placeholder="0"
                          style="width: 100%"
                        />
                      </el-form-item>

                      <el-form-item label="Маржа" prop="margin">
                        <el-input-number
                          v-model="resultForm.margin"
                          :min="0"
                          :precision="0"
                          :step="1000"
                          controls-position="right"
                          placeholder="0"
                          style="width: 100%"
                        />
                      </el-form-item>
                    </div>

                    <el-button
                      class="call-contacts__submit-button"
                      type="success"
                      icon="el-icon-check"
                      :loading="resultLoading"
                      @click="submitPhoneResult"
                    >
                      Сохранить результат
                    </el-button>
                  </el-form>
                </section>
              </transition>
            </article>
          </div>

          <el-pagination
            v-if="assignedTotal > assignedPagination.limit"
            class="call-contacts__pagination"
            small
            layout="prev, pager, next"
            :current-page="assignedPagination.page"
            :page-size="assignedPagination.limit"
            :total="assignedTotal"
            @current-change="handleAssignedPageChange"
          />
        </section>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios"
import moment from "moment"
import { handelerErrors } from "@/plugins/utils"
import Fetch from "./mixins/Fetch"

const callContactsApi = axios.create({
  baseURL: "https://crm.liftnet.ru/wr-contacts/",
})

export default {
  name: "CallContacts",
  mixins: [Fetch],
  data() {
    return {
      loading: false,
      departmentsLoading: false,
      directionsLoading: false,
      registerLoading: false,
      phoneLoading: false,
      noPhonesAvailable: false,
      resultLoading: false,
      assignedLoading: false,
      overviewLoading: false,
      detailLoading: false,
      detailPhoneId: null,
      registeredUser: null,
      activeWorkTab: "phones",
      currentPhone: null,
      isEditingAssignedPhone: false,
      expandedAssignedPhoneId: null,
      departments: [],
      directions: [],
      assignedPhones: [],
      overview: {
        managers: [],
        summary: null,
        chart: [],
        manager_stats: [],
      },
      overviewFilters: {
        manager_id: "",
        dateRange: [],
      },
      assignedTotal: 0,
      assignedPagination: {
        page: 1,
        limit: 20,
      },
      assignedFilters: {
        date: "",
      },
      reasonOptions: [
        "Не звонить больше",
        "Не интересно",
        "Пока не планируем",
      ],
      roleOptions: [
        { value: "manager", label: "Менеджер" },
        { value: "rop", label: "РОП" },
        { value: "director", label: "Директор" },
      ],
      errorMessage: "",
      form: {
        id_crm: null,
        full_name: "",
        role: "manager",
        department: "",
        direction: "",
      },
      resultForm: {
        answered: null,
        reason: "",
        has_equipment: "",
        has_need: null,
        equipment_direction: "",
        comment: "",
        revenue: null,
        margin: null,
      },
      rules: {
        id_crm: [
          { required: true, message: "Не найден ID CRM", trigger: "change" },
        ],
        full_name: [
          { required: true, message: "Введите ФИО", trigger: "blur" },
        ],
        role: [
          { required: true, message: "Выберите роль", trigger: "change" },
        ],
      },
      managerLocationRules: {
        department: [
          { required: true, message: "Выберите департамент", trigger: "change" },
        ],
        direction: [
          { required: true, message: "Выберите направление", trigger: "change" },
        ],
      },
      resultRules: {
        answered: [
          { required: true, message: "Укажите, ответил ли клиент", trigger: "change" },
        ],
        has_equipment: [
          { required: true, message: "Укажите наличие техники", trigger: "change" },
        ],
        has_need: [
          { required: true, message: "Укажите наличие потребности", trigger: "change" },
        ],
      },
    }
  },
  computed: {
    normalizedDepartments() {
      return this.normalizeList(this.departments, "department")
        .filter((department) => department.value !== "Общий")
    },
    normalizedDirections() {
      return this.normalizeList(this.directions, "direction")
    },
    registrationRules() {
      if (this.form.role === "director") {
        return {
          ...this.rules,
          department: this.managerLocationRules.department,
        }
      }

      return {
        ...this.rules,
        ...this.managerLocationRules,
      }
    },
    isSupervisor() {
      return this.registeredUser && ["rop", "director"].includes(this.registeredUser.role)
    },
    canReceivePhones() {
      return this.registeredUser && ["manager", "rop"].includes(this.registeredUser.role)
    },
    hasWorkTabs() {
      return this.registeredUser && this.registeredUser.role === "rop"
    },
    showPhonePanel() {
      if (!this.canReceivePhones) {
        return false
      }

      return !this.hasWorkTabs || this.activeWorkTab === "phones"
    },
    showOverviewPanel() {
      if (!this.isSupervisor) {
        return false
      }

      return !this.hasWorkTabs || this.activeWorkTab === "stats"
    },
    currentRoleLabel() {
      if (!this.registeredUser) {
        return ""
      }

      const role = this.roleOptions.find((item) => item.value === this.registeredUser.role)
      return role ? role.label : this.registeredUser.role
    },
    overviewManagers() {
      return this.overview && Array.isArray(this.overview.managers) ? this.overview.managers : []
    },
    overviewSummary() {
      return this.overview && this.overview.summary ? this.overview.summary : {
        today_calls: 0,
        period_calls: 0,
        connected_managers: 0,
        processed_calls: 0,
        need_calls: 0,
      }
    },
    overviewManagerStats() {
      return this.overview && Array.isArray(this.overview.manager_stats)
        ? this.overview.manager_stats
        : []
    },
    overviewCards() {
      return [
        { label: "Звонки сегодня", value: this.formatIntegerValue(this.overviewSummary.today_calls) },
        { label: "Менеджеров подключено", value: this.formatIntegerValue(this.overviewSummary.connected_managers) },
        { label: "Звонки за период", value: this.formatIntegerValue(this.overviewSummary.period_calls) },
        { label: "Потребности", value: this.formatIntegerValue(this.overviewSummary.need_calls) },
      ]
    },
    chartGridLines() {
      return [40, 86, 132, 178]
    },
    overviewChartDots() {
      const chart = this.overview && Array.isArray(this.overview.chart) ? this.overview.chart : []
      const maxCalls = Math.max(...chart.map((item) => Number(item.calls) || 0), 1)
      const width = 592
      const left = 28
      const top = 28
      const height = 156
      const step = chart.length > 1 ? width / (chart.length - 1) : 0

      return chart.map((item, index) => {
        const calls = Number(item.calls) || 0
        const x = left + (step * index)
        const y = top + height - ((calls / maxCalls) * height)

        return {
          date: item.date,
          label: moment(item.date).format("DD.MM"),
          calls,
          x,
          y,
        }
      })
    },
    overviewChartPoints() {
      return this.overviewChartDots.map((point) => `${point.x},${point.y}`).join(" ")
    },
    overviewChartAreaPoints() {
      if (!this.overviewChartDots.length) {
        return ""
      }

      const first = this.overviewChartDots[0]
      const last = this.overviewChartDots[this.overviewChartDots.length - 1]

      return [
        `${first.x},184`,
        this.overviewChartPoints,
        `${last.x},184`,
      ].join(" ")
    },
  },
  methods: {
    async fetchData() {
      this.errorMessage = ""
      this.registeredUser = null
      this.activeWorkTab = "phones"
      this.currentPhone = null
      this.noPhonesAvailable = false
      this.isEditingAssignedPhone = false
      this.expandedAssignedPhoneId = null
      this.detailPhoneId = null
      this.assignedPhones = []
      this.resetOverview()
      this.assignedTotal = 0
      this.assignedPagination.page = 1
      this.departments = []
      this.directions = []
      this.resetResultForm()
      this.form.id_crm = localStorage.getItem("used-id")
      this.form.full_name = localStorage.getItem("user-fio") || ""
      this.form.role = "manager"
      this.form.department = ""
      this.form.direction = ""

      if (!this.form.id_crm) {
        this.errorMessage = "Не удалось получить ID CRM пользователя."
        return
      }

      await this.getCurrentUser()
    },
    async getCurrentUser() {
      try {
        this.loading = true
        const { data } = await callContactsApi.get("/api/users", {
          params: {
            id_crm: this.form.id_crm,
          },
        })

        const user = this.normalizeUser(data)

        if (!user) {
          await this.getRegistrationOptions()
          return
        }

        this.registeredUser = user
        this.activeWorkTab = user.role === "director" ? "stats" : "phones"
        await this.loadUserWorkspace()
      }
      catch (error) {
        const status = error.response && error.response.status

        if (status === 404 || !error.response) {
          await this.getRegistrationOptions()
          return
        }

        this.errorMessage = "Не удалось загрузить данные пользователя."
        handelerErrors(error)
      }
      finally {
        this.loading = false
      }
    },
    async getRegistrationOptions() {
      await Promise.all([
        this.getDepartments(),
        this.getDirections(),
      ])
    },
    async loadUserWorkspace() {
      if (this.registeredUser && this.registeredUser.role === "director") {
        this.activeWorkTab = "stats"
        await this.getOverview()
        return
      }

      if (this.registeredUser && this.registeredUser.role === "rop") {
        await Promise.all([
          this.getAssignedPhones(),
          this.getOverview(),
        ])
        return
      }

      if (this.canReceivePhones) {
        await this.getAssignedPhones()
      }
    },
    async getDepartments() {
      try {
        this.departmentsLoading = true
        const { data } = await callContactsApi.get("/api/users/departments")

        this.departments = this.unwrapList(data, "departments")
      }
      catch (error) {
        this.errorMessage = "Не удалось загрузить список департаментов."
        handelerErrors(error)
      }
      finally {
        this.departmentsLoading = false
      }
    },
    async getDirections() {
      try {
        this.directionsLoading = true
        const { data } = await callContactsApi.get("/api/users/directions")

        this.directions = this.unwrapList(data, "directions")
      }
      catch (error) {
        this.errorMessage = "Не удалось загрузить список направлений."
        handelerErrors(error)
      }
      finally {
        this.directionsLoading = false
      }
    },
    registerUser() {
      this.$refs.registerForm.validate(async (valid) => {
        if (!valid) {
          return
        }

        try {
          this.registerLoading = true
          this.errorMessage = ""

          const payload = {
            id_crm: this.form.id_crm,
            full_name: this.form.full_name,
            role: this.form.role,
            department: this.form.department,
            direction: this.form.role === "director" ? null : this.form.direction,
          }

          const { data } = await callContactsApi.post("/api/users/register", payload)

          this.registeredUser = this.normalizeUser(data) || payload
          this.activeWorkTab = this.registeredUser.role === "director" ? "stats" : "phones"
          await this.loadUserWorkspace()
          this.$notify.success({
            title: "Готово",
            message: "Пользователь зарегистрирован.",
          })
        }
        catch (error) {
          if (error.response && error.response.status === 409) {
            this.errorMessage = this.getRegisterConflictMessage(error)
            return
          }

          this.errorMessage = "Не удалось зарегистрировать пользователя."
          handelerErrors(error)
        }
        finally {
          this.registerLoading = false
        }
      })
    },
    handleRoleChange(role) {
      if (role === "director") {
        this.form.direction = ""
      }

      this.$nextTick(() => {
        if (this.$refs.registerForm && typeof this.$refs.registerForm.clearValidate === "function") {
          this.$refs.registerForm.clearValidate()
        }
      })
    },
    handleWorkTabClick(tab) {
      const tabName = tab && tab.name ? tab.name : this.activeWorkTab

      if (tabName === "stats") {
        this.getOverview()
        return
      }

      if (tabName === "phones") {
        this.getAssignedPhones()
      }
    },
    getRegisterConflictMessage(error) {
      const detail = error &&
        error.response &&
        error.response.data &&
        error.response.data.detail

      if (detail === "Director for this department already registered") {
        return "Директор этого департамента уже зарегистрирован."
      }

      if (detail === "ROP for this department and direction already registered") {
        return "РОП этого департамента и направления уже зарегистрирован."
      }

      return "Этот ID CRM уже зарегистрирован."
    },
    async getOverview() {
      if (!this.registeredUser || !this.form.id_crm) {
        return
      }

      try {
        this.overviewLoading = true
        this.errorMessage = ""

        const params = {
          supervisor_id: this.form.id_crm,
        }

        if (this.overviewFilters.manager_id) {
          params.manager_id = this.overviewFilters.manager_id
        }

        if (Array.isArray(this.overviewFilters.dateRange) && this.overviewFilters.dateRange.length === 2) {
          params.date_from = this.overviewFilters.dateRange[0]
          params.date_to = this.overviewFilters.dateRange[1]
        }

        const { data } = await callContactsApi.get("/api/phones/overview", {
          params,
        })

        this.overview = {
          managers: data && data.managers ? data.managers : [],
          summary: data && data.summary ? data.summary : null,
          chart: data && data.chart ? data.chart : [],
          manager_stats: data && data.manager_stats ? data.manager_stats : [],
        }
      }
      catch (error) {
        this.errorMessage = "Не удалось загрузить статистику менеджеров."
        handelerErrors(error)
      }
      finally {
        this.overviewLoading = false
      }
    },
    handleOverviewFiltersChange() {
      this.getOverview()
    },
    resetOverview() {
      this.overview = {
        managers: [],
        summary: null,
        chart: [],
        manager_stats: [],
      }
      this.overviewFilters = {
        manager_id: "",
        dateRange: [],
      }
    },
    async getNextPhone() {
      try {
        this.phoneLoading = true
        this.errorMessage = ""

        const { data } = await callContactsApi.post("/api/phones/next", {
          manager_id: this.form.id_crm,
        })

        const nextPhone = data && Object.prototype.hasOwnProperty.call(data, "phone")
          ? data.phone
          : data

        if (!nextPhone || !nextPhone.phone) {
          this.currentPhone = null
          this.noPhonesAvailable = true
          this.isEditingAssignedPhone = false
          this.resetResultForm()
          await this.getAssignedPhones()
          return
        }

        this.currentPhone = nextPhone
        this.noPhonesAvailable = false
        this.isEditingAssignedPhone = false
        this.expandedAssignedPhoneId = null
        this.resetResultForm()
        await this.getAssignedPhones()
      }
      catch (error) {
        const status = error.response && error.response.status

        if (status === 404) {
          this.noPhonesAvailable = true
          return
        }

        this.errorMessage = "Не удалось получить телефон."
        handelerErrors(error)
      }
      finally {
        this.phoneLoading = false
      }
    },
    async getAssignedPhones() {
      if (!this.registeredUser || !this.form.id_crm) {
        return
      }

      try {
        this.assignedLoading = true
        this.errorMessage = ""

        const params = {
          manager_id: this.form.id_crm,
          page: this.assignedPagination.page,
          limit: this.assignedPagination.limit,
        }

        if (this.assignedFilters.date) {
          params.date = this.assignedFilters.date
        }

        const { data } = await callContactsApi.get("/api/phones/assigned", {
          params,
        })

        this.assignedPhones = data && data.items ? data.items : []
        this.assignedTotal = data && data.total ? data.total : 0
        this.assignedPagination.page = data && data.page ? data.page : this.assignedPagination.page
        this.assignedPagination.limit = data && data.limit ? data.limit : this.assignedPagination.limit
        this.syncAssignedTableExpansion()
      }
      catch (error) {
        this.errorMessage = "Не удалось загрузить список выданных номеров."
        handelerErrors(error)
      }
      finally {
        this.assignedLoading = false
      }
    },
    async openAssignedPhone(phoneId) {
      if (this.isEditingAssignedPhone && this.expandedAssignedPhoneId === phoneId) {
        this.collapseAssignedPhone()
        return
      }

      try {
        this.detailLoading = true
        this.detailPhoneId = phoneId
        this.errorMessage = ""

        const { data } = await callContactsApi.get(`/api/phones/assigned/${phoneId}`, {
          params: {
            manager_id: this.form.id_crm,
          },
        })

        this.currentPhone = data && data.phone ? data.phone : null
        this.isEditingAssignedPhone = true
        this.expandedAssignedPhoneId = phoneId
        this.fillResultForm(data && data.result ? data.result : null)
        this.$nextTick(this.syncAssignedTableExpansion)
      }
      catch (error) {
        this.errorMessage = "Не удалось загрузить данные выданного номера."
        handelerErrors(error)
      }
      finally {
        this.detailLoading = false
        this.detailPhoneId = null
      }
    },
    handleAssignedPageChange(page) {
      this.assignedPagination.page = page
      this.collapseAssignedPhone()
      this.getAssignedPhones()
    },
    handleAssignedDateChange() {
      this.assignedPagination.page = 1
      this.collapseAssignedPhone()
      this.getAssignedPhones()
    },
    collapseAssignedPhone() {
      this.currentPhone = null
      this.isEditingAssignedPhone = false
      this.expandedAssignedPhoneId = null
      this.resetResultForm()
      this.$nextTick(this.syncAssignedTableExpansion)
    },
    isAssignedPhoneExpanded(phone) {
      return Boolean(
        this.isEditingAssignedPhone &&
        this.currentPhone &&
        phone &&
        this.expandedAssignedPhoneId === phone.id
      )
    },
    syncAssignedTableExpansion() {
      const table = this.$refs.assignedTable

      if (!table || typeof table.toggleRowExpansion !== "function") {
        return
      }

      this.assignedPhones.forEach((phone) => {
        table.toggleRowExpansion(
          phone,
          this.isEditingAssignedPhone && this.expandedAssignedPhoneId === phone.id
        )
      })
    },
    getResultFormRefs() {
      const refs = this.$refs.resultForm

      if (!refs) {
        return []
      }

      return Array.isArray(refs) ? refs : [refs]
    },
    clearResultFormValidation(prop) {
      this.getResultFormRefs().forEach((form) => {
        if (form && typeof form.clearValidate === "function") {
          form.clearValidate(prop)
        }
      })
    },
    validateResultForm(callback) {
      const form = this.getResultFormRefs()[0]

      if (!form || typeof form.validate !== "function") {
        callback(false)
        return
      }

      form.validate(callback)
    },
    handleAnsweredChange(answered) {
      if (answered !== true) {
        this.resultForm.reason = ""
        this.resultForm.has_equipment = ""
        this.resultForm.has_need = null
        this.resultForm.equipment_direction = ""
        this.resultForm.comment = ""
        this.resultForm.revenue = null
        this.resultForm.margin = null
      }

      this.$nextTick(() => {
        this.clearResultFormValidation()
      })
    },
    handleHasNeedChange(hasNeed) {
      if (hasNeed !== true) {
        this.resultForm.equipment_direction = ""
        this.resultForm.revenue = null
        this.resultForm.margin = null
      }

      this.$nextTick(() => {
        this.clearResultFormValidation("equipment_direction")
      })
    },
    submitPhoneResult() {
      if (!this.currentPhone || !this.currentPhone.id) {
        this.errorMessage = "Сначала получите телефон."
        return
      }

      this.validateResultForm(async (valid) => {
        if (!valid) {
          return
        }

        try {
          this.resultLoading = true
          this.errorMessage = ""

          const shouldSendEquipmentFields = this.resultForm.answered === true
          const shouldSendEquipmentDirection = shouldSendEquipmentFields && this.resultForm.has_need === true
          const shouldSendFinancialFields = this.isEditingAssignedPhone && shouldSendEquipmentDirection

          const payload = {
            manager_id: this.form.id_crm,
            manager_name: this.registeredUser.full_name,
            manager_department: this.registeredUser.department,
            answered: this.resultForm.answered,
            reason: shouldSendEquipmentFields ? this.resultForm.reason : "",
            has_equipment: shouldSendEquipmentFields ? this.resultForm.has_equipment : "",
            has_need: shouldSendEquipmentFields ? this.resultForm.has_need : null,
            equipment_direction: shouldSendEquipmentDirection ? this.resultForm.equipment_direction : "",
            comment: shouldSendEquipmentFields ? this.resultForm.comment : "",
            revenue: shouldSendFinancialFields ? this.normalizeOptionalInteger(this.resultForm.revenue) : null,
            margin: shouldSendFinancialFields ? this.normalizeOptionalInteger(this.resultForm.margin) : null,
          }

          await callContactsApi.post(`/api/phones/${this.currentPhone.id}/result`, payload)

          this.$notify.success({
            title: "Готово",
            message: "Результат обработки сохранен.",
          })

          this.currentPhone = null
          this.isEditingAssignedPhone = false
          this.expandedAssignedPhoneId = null
          this.resetResultForm()
          await this.getAssignedPhones()
        }
        catch (error) {
          this.errorMessage = "Не удалось сохранить результат обработки."
          handelerErrors(error)
        }
        finally {
          this.resultLoading = false
        }
      })
    },
    resetResultForm() {
      this.resultForm = {
        answered: null,
        reason: "",
        has_equipment: "",
        has_need: null,
        equipment_direction: "",
        comment: "",
        revenue: null,
        margin: null,
      }

      this.$nextTick(() => {
        this.clearResultFormValidation()
      })
    },
    fillResultForm(result) {
      if (!result) {
        this.resetResultForm()
        return
      }

      this.resultForm = {
        answered: this.normalizeBooleanField(result.answered),
        reason: result.reason || "",
        has_equipment: result.has_equipment || "",
        has_need: this.normalizeBooleanField(result.has_need),
        equipment_direction: result.equipment_direction || "",
        comment: result.comment || "",
        revenue: this.normalizeOptionalInteger(result.revenue),
        margin: this.normalizeOptionalInteger(result.margin),
      }

      this.$nextTick(() => {
        this.clearResultFormValidation()
      })
    },
    normalizeBooleanField(value) {
      if (value === true || value === false) {
        return value
      }

      if (value === 1 || value === "true" || value === "Да") {
        return true
      }

      if (value === 0 || value === "false" || value === "Нет") {
        return false
      }

      return null
    },
    normalizeOptionalInteger(value) {
      if (value === null || value === undefined || value === "") {
        return null
      }

      const parsed = Number(value)

      if (!Number.isFinite(parsed)) {
        return null
      }

      return Math.trunc(parsed)
    },
    formatPhoneCreatedDate(phone) {
      const rawDate = phone && (
        phone.sent_at ||
        phone.created_at ||
        phone.createdAt ||
        phone.created ||
        phone.date_created ||
        phone.assigned_at
      )

      if (!rawDate) {
        return "—"
      }

      const date = moment(rawDate)

      return date.isValid() ? date.format("DD.MM.YYYY HH:mm") : rawDate
    },
    formatPhoneNumber(phone) {
      if (!phone) {
        return "—"
      }

      const digits = String(phone).replace(/\D/g, "")

      if (digits.length === 11) {
        return `${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`
      }

      if (digits.length === 10) {
        return `7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`
      }

      return phone
    },
    getAssignedResultValue(phone, field) {
      if (!phone) {
        return null
      }

      if (phone[field] !== undefined && phone[field] !== null) {
        return phone[field]
      }

      const result = phone.result || phone.processing_result || phone.phone_result

      return result && result[field] !== undefined ? result[field] : null
    },
    hasAssignedTextValue(phone, field) {
      const value = this.getAssignedResultValue(phone, field)

      return value !== null && String(value).trim() !== ""
    },
    hasAssignedNeed(phone) {
      return this.normalizeBooleanField(this.getAssignedResultValue(phone, "has_need")) === true
    },
    getAssignedStatusText(phone) {
      if (this.hasAssignedNeed(phone)) {
        return "Потребность"
      }

      return phone && phone.is_processed ? "Обработан" : "В работе"
    },
    getAssignedStatusType(phone) {
      if (this.hasAssignedNeed(phone)) {
        return "danger"
      }

      return phone && phone.is_processed ? "success" : "warning"
    },
    getAssignedInfoText(phone) {
      if (!this.hasAssignedInfoResult(phone)) {
        return phone && phone.source_name ? phone.source_name : "—"
      }

      const parts = []
      const equipmentDirection = this.getAssignedResultValue(phone, "equipment_direction")
      const comment = this.getAssignedResultValue(phone, "comment")
      const revenue = this.getAssignedResultValue(phone, "revenue")
      const margin = this.getAssignedResultValue(phone, "margin")

      if (this.hasAssignedTextValue(phone, "equipment_direction")) {
        parts.push(`Направление: ${String(equipmentDirection).trim()}`)
      }

      if (this.hasAssignedTextValue(phone, "comment")) {
        parts.push(`Комментарий: ${String(comment).trim()}`)
      }

      if (this.hasAssignedTextValue(phone, "revenue")) {
        parts.push(`Выручка: ${this.formatIntegerValue(revenue)}`)
      }

      if (this.hasAssignedTextValue(phone, "margin")) {
        parts.push(`Маржа: ${this.formatIntegerValue(margin)}`)
      }

      return parts.join("; ")
    },
    getAssignedInfoItems(phone) {
      if (!this.hasAssignedInfoResult(phone)) {
        return [
          {
            label: "Источник",
            value: phone && phone.source_name ? phone.source_name : "—",
          },
        ]
      }

      const items = []
      const equipmentDirection = this.getAssignedResultValue(phone, "equipment_direction")
      const comment = this.getAssignedResultValue(phone, "comment")
      const revenue = this.getAssignedResultValue(phone, "revenue")
      const margin = this.getAssignedResultValue(phone, "margin")

      if (this.hasAssignedTextValue(phone, "equipment_direction")) {
        items.push({
          label: "Направление техники",
          value: String(equipmentDirection).trim(),
        })
      }

      if (this.hasAssignedTextValue(phone, "comment")) {
        items.push({
          label: "Комментарий",
          value: String(comment).trim(),
        })
      }

      if (this.hasAssignedTextValue(phone, "revenue")) {
        items.push({
          label: "Выручка",
          value: this.formatIntegerValue(revenue),
        })
      }

      if (this.hasAssignedTextValue(phone, "margin")) {
        items.push({
          label: "Маржа",
          value: this.formatIntegerValue(margin),
        })
      }

      return items
    },
    hasAssignedInfoResult(phone) {
      return this.hasAssignedTextValue(phone, "equipment_direction") ||
        this.hasAssignedTextValue(phone, "comment") ||
        this.hasAssignedTextValue(phone, "revenue") ||
        this.hasAssignedTextValue(phone, "margin")
    },
    formatIntegerValue(value) {
      const normalized = this.normalizeOptionalInteger(value)

      if (normalized === null) {
        return "—"
      }

      return normalized.toLocaleString("ru-RU")
    },
    normalizeUser(data) {
      const user = data && data.user ? data.user : data

      if (!user || !user.id_crm || !user.full_name || !user.department) {
        return null
      }

      const role = user.role || "manager"

      if (role !== "director" && !user.direction) {
        return null
      }

      return {
        id_crm: user.id_crm,
        full_name: user.full_name,
        role,
        department: user.department,
        direction: user.direction || null,
      }
    },
    unwrapList(data, key) {
      if (Array.isArray(data)) {
        return data
      }

      if (!data) {
        return []
      }

      return data[key] || data.data || []
    },
    normalizeList(items, field) {
      return items
        .map((item) => {
          if (typeof item === "string") {
            return {
              label: item,
              value: item,
            }
          }

          const value = item[field] || item.name || item.title || item.value

          return {
            label: value,
            value,
          }
        })
        .filter((item) => item.value)
    },
  },
}
</script>

<style lang="scss">
.call-contacts {
  --cc-bg: #f4f6f8;
  --cc-surface: #ffffff;
  --cc-surface-soft: #f8fafc;
  --cc-border: #dfe5ec;
  --cc-border-strong: #c7d1dc;
  --cc-text: #1d2936;
  --cc-muted: #687586;
  --cc-faint: #8a96a5;
  --cc-blue: #1f6feb;
  --cc-blue-soft: #eaf2ff;
  --cc-green: #2f9e44;
  --cc-green-soft: #edf8ef;
  --cc-amber: #c17a12;
  --cc-amber-soft: #fff6e5;

  min-height: calc(100vh - 76px);
  padding: 22px;
  background:
    linear-gradient(180deg, #f7f9fb 0, var(--cc-bg) 260px),
    var(--cc-bg);
  color: var(--cc-text);

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  .call-contacts__panel {
    max-width: 1180px;
    margin: 0 auto;
  }

  .el-button {
    min-height: 34px;
    border-radius: 6px;
    font-weight: 600;
  }

  .el-input__inner,
  .el-textarea__inner {
    border-radius: 6px;
  }

  .call-contacts__head,
  .call-contacts__start,
  .call-contacts__phone,
  .call-contacts__assigned {
    background: var(--cc-surface);
    border: 1px solid var(--cc-border);
    border-radius: 8px;
    box-shadow: 0 10px 26px rgba(29, 41, 54, 0.05);
  }

  .call-contacts__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
    padding: 16px 18px;

    p {
      margin: 0;
      color: var(--cc-muted);
      font-size: 14px;
      line-height: 1.45;
    }
  }

  .call-contacts__alert {
    margin-bottom: 14px;
  }

  .call-contacts__form {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 14px;
    padding: 18px;
    background: var(--cc-surface);
    border: 1px solid var(--cc-border);
    border-radius: 8px;

    .el-form-item:first-child,
    .el-button {
      grid-column: 1 / -1;
    }

    .el-button {
      justify-self: start;
      min-width: 190px;
    }
  }

  .call-contacts__role-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .el-radio-button {
      margin: 0;
    }

    .el-radio-button__inner {
      border: 1px solid var(--cc-border-strong);
      border-radius: 6px;
      box-shadow: none;
      font-weight: 700;
    }

    .el-radio-button:first-child .el-radio-button__inner,
    .el-radio-button:last-child .el-radio-button__inner {
      border-radius: 6px;
    }
  }

  .call-contacts__work {
    display: grid;
    gap: 14px;
  }

  .call-contacts__tabs {
    padding: 0 4px;

    .el-tabs__header {
      margin: 0;
    }

    .el-tabs__nav-wrap::after {
      height: 1px;
      background: var(--cc-border);
    }

    .el-tabs__item {
      height: 38px;
      color: var(--cc-muted);
      font-weight: 800;
      line-height: 38px;
    }

    .el-tabs__item.is-active {
      color: var(--cc-blue);
    }
  }

  .call-contacts__overview,
  .call-contacts__chart-panel,
  .call-contacts__managers-panel {
    background: var(--cc-surface);
    border: 1px solid var(--cc-border);
    border-radius: 8px;
    box-shadow: 0 10px 26px rgba(29, 41, 54, 0.05);
  }

  .call-contacts__overview {
    display: grid;
    gap: 14px;
    padding: 16px 18px 18px;
  }

  .call-contacts__section-head--overview {
    align-items: center;
  }

  .call-contacts__filters--overview {
    .el-select {
      width: 260px;
    }

    .el-date-editor {
      width: 300px;
    }
  }

  .call-contacts__kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
  }

  .call-contacts__kpi-card {
    display: grid;
    gap: 7px;
    min-width: 0;
    padding: 14px;
    border: 1px solid #dce7f2;
    border-radius: 8px;
    background:
      linear-gradient(180deg, #fbfdff 0%, #f7fafc 100%),
      var(--cc-surface-soft);

    span {
      color: var(--cc-faint);
      font-size: 11px;
      font-weight: 800;
      line-height: 1.2;
      text-transform: uppercase;
    }

    strong {
      color: var(--cc-text);
      font-size: 26px;
      font-weight: 850;
      line-height: 1.05;
    }
  }

  .call-contacts__overview-grid {
    display: grid;
    grid-template-columns: minmax(360px, 0.88fr) minmax(440px, 1.12fr);
    gap: 14px;
    align-items: start;
  }

  .call-contacts__chart-panel,
  .call-contacts__managers-panel {
    display: grid;
    gap: 12px;
    padding: 14px;
    box-shadow: none;
  }

  .call-contacts__section-head--compact {
    align-items: flex-start;

    h3 {
      font-size: 16px;
    }
  }

  .call-contacts__chart {
    display: grid;
    gap: 8px;
    min-width: 0;

    svg {
      width: 100%;
      height: auto;
      overflow: visible;
    }
  }

  .call-contacts__chart-grid {
    stroke: #e6edf5;
    stroke-width: 1;
  }

  .call-contacts__chart-area {
    fill: rgba(31, 111, 235, 0.12);
  }

  .call-contacts__chart-line {
    fill: none;
    stroke: var(--cc-blue);
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 4;
  }

  .call-contacts__chart-dot {
    fill: #ffffff;
    stroke: var(--cc-blue);
    stroke-width: 3;
  }

  .call-contacts__chart-value {
    fill: #506074;
    font-size: 12px;
    font-weight: 800;
  }

  .call-contacts__chart-labels {
    display: grid;
    grid-template-columns: repeat(10, minmax(0, 1fr));
    gap: 3px;
    color: var(--cc-faint);
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
  }

  .call-contacts__overview-table {
    .cell {
      justify-content: flex-start;
    }

    .is-right .cell {
      justify-content: flex-end;
    }
  }

  .call-contacts__manager-name {
    min-width: 0;
    color: var(--cc-text);
    font-size: 16px;
    font-weight: 800;
    line-height: 1.25;
    word-break: break-word;
  }

  .call-contacts__manager-metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;

    span {
      display: grid;
      gap: 3px;
      min-width: 0;
      padding: 9px;
      color: var(--cc-faint);
      font-size: 11px;
      font-weight: 800;
      line-height: 1.2;
      text-transform: uppercase;
      background: #f8fafc;
      border: 1px solid #e6edf5;
      border-radius: 8px;
    }

    strong {
      color: #2d3b4c;
      font-size: 15px;
      line-height: 1.25;
      text-transform: none;
      word-break: break-word;
    }
  }

  .call-contacts__start {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 16px 18px;

    p {
      margin: 3px 0 0;
      color: var(--cc-muted);
      font-size: 14px;
      line-height: 1.4;
    }
  }

  .call-contacts__start--empty {
    border-color: #cfe0f5;
    background:
      linear-gradient(135deg, #f8fbff 0%, #ffffff 64%),
      var(--cc-surface);
  }

  .call-contacts__queue-empty {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    strong {
      display: block;
      margin-top: 2px;
      color: var(--cc-text);
      font-size: 18px;
      font-weight: 800;
      line-height: 1.25;
    }
  }

  .call-contacts__queue-icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    color: var(--cc-blue);
    font-size: 18px;
    background: var(--cc-blue-soft);
    border: 1px solid #cfe0fb;
    border-radius: 8px;
  }

  .call-contacts__eyebrow {
    display: block;
    color: var(--cc-faint);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1.2;
    text-transform: uppercase;
  }

  .call-contacts__next-button {
    min-width: 180px;
  }

  .call-contacts__phone {
    position: relative;
    display: grid;
    gap: 14px;
    padding: 18px;
    border-color: #cbd9ea;
  }

  .call-contacts__phone--editing {
    padding-top: 18px;
  }

  .call-contacts__phone--inline {
    margin: 4px 0 6px;
    border-color: #c9d9ee;
    background:
      linear-gradient(180deg, #fbfdff 0%, #ffffff 74%),
      var(--cc-surface);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92), 0 14px 30px rgba(31, 111, 235, 0.08);
  }

  .call-contacts__phone-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .call-contacts__phone--editing .call-contacts__phone-head {
    padding-right: 128px;
  }

  .call-contacts__caption {
    display: block;
    margin-bottom: 4px;
    color: var(--cc-faint);
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
  }

  .call-contacts__number,
  .call-contacts__table-phone,
  .call-contacts__mobile-phone {
    color: var(--cc-text);
    text-decoration: none;
  }

  .call-contacts__number {
    display: inline-block;
    font-size: 28px;
    font-weight: 800;
    line-height: 1.1;
    word-break: break-word;
  }

  .call-contacts__collapse-button {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 30px;
    min-height: 30px;
    min-width: 0;
    margin: 0;
    padding: 0 10px;
    color: #46617f;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    background: #f5f8fc;
    border: 1px solid #d5e0ed;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(29, 41, 54, 0.04);
    transition: color 0.16s ease, background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
  }

  .call-contacts__collapse-button:hover,
  .call-contacts__collapse-button:focus {
    color: var(--cc-blue);
    background: #eef5ff;
    border-color: #bcd3f8;
    box-shadow: 0 3px 8px rgba(31, 111, 235, 0.12);
  }

  .call-contacts__collapse-label {
    display: inline;
  }

  .call-contacts__meta-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 0;
    overflow: hidden;
    border: 1px solid var(--cc-border);
    border-radius: 8px;
    background: var(--cc-surface-soft);

    div {
      min-width: 0;
      padding: 12px 14px;
      border-right: 1px solid var(--cc-border);
    }

    div:last-child {
      border-right: 0;
    }

    dt {
      margin: 0 0 4px;
      color: var(--cc-faint);
      font-size: 12px;
      font-weight: 700;
      line-height: 1.2;
    }

    dd {
      min-width: 0;
      margin: 0;
      color: #344253;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.35;
      word-break: break-word;
    }
  }

  .call-contacts__result-form {
    display: grid;
    grid-template-columns: minmax(220px, 0.8fr) minmax(280px, 1fr);
    gap: 2px 18px;
    align-items: start;
    padding-top: 2px;

    .el-form-item {
      margin-bottom: 14px;
    }

    .el-form-item__label {
      padding-bottom: 6px;
      color: var(--cc-text);
      font-weight: 700;
      line-height: 1.25;
    }

    .call-contacts__result-form-row {
      grid-column: 1 / -1;
    }
  }

  .call-contacts__financial-fields {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: repeat(2, minmax(180px, 1fr));
    gap: 2px 18px;
  }

  .call-contacts__radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 18px;
    line-height: 1.4;

    .el-radio {
      margin-right: 0;
    }
  }

  .call-contacts__submit-button {
    grid-column: 1 / -1;
    justify-self: end;
    min-width: 210px;
    padding-right: 22px;
    padding-left: 22px;
  }

  .call-contacts__assigned {
    display: grid;
    gap: 12px;
    padding: 16px 18px 18px;
  }

  .call-contacts__section-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;

    h3 {
      margin: 3px 0 0;
      color: var(--cc-text);
      font-size: 18px;
      font-weight: 800;
      line-height: 1.25;
    }
  }

  .call-contacts__filters {
    display: flex;
    align-items: center;
    gap: 8px;

    .el-date-editor {
      width: 220px;
    }
  }

  .call-contacts__table {
    width: 100%;
    border: 1px solid var(--cc-border);
    border-radius: 8px;
    overflow: hidden;

    &::before {
      display: none;
    }

    th {
      background: #f7f9fb;
      color: var(--cc-muted);
      font-weight: 800;
    }

    td {
      vertical-align: top;
    }

    .el-table__expand-column {
      padding: 0;
    }

    .el-table__expand-column .cell {
      min-height: 0;
      padding: 0;
    }

    .el-table__expand-icon {
      display: none;
    }

    .el-table__expanded-cell {
      padding: 0 12px 14px 12px;
      background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
      border-bottom-color: #e8edf2;
    }

    .el-table__expanded-cell[class*="cell"] {
      box-shadow: inset 0 1px 0 #eef4fb;
    }

    .cell {
      display: flex;
      align-items: flex-start;
      min-height: 48px;
      padding-top: 4px;
      padding-bottom: 4px;
      line-height: 1.35;
    }

    td,
    th.is-leaf {
      border-bottom-color: #e8edf2;
    }

    .el-table__row:hover > td {
      background: #f8fbff;
    }
  }

  .call-contacts__table-phone {
    font-weight: 700;
  }

  .call-contacts__status-tag {
    display: inline-flex;
    justify-content: center;
    min-width: 92px;
    font-weight: 800;
  }

  .call-contacts__assigned-info {
    display: grid;
    gap: 7px;
    min-width: 0;
    width: 100%;
    padding: 4px 0;
  }

  .call-contacts__assigned-info--result {
    max-width: 560px;
  }

  .call-contacts__assigned-info-item {
    display: grid;
    gap: 3px;
    min-width: 0;

    span {
      color: var(--cc-faint);
      font-size: 10px;
      font-weight: 800;
      line-height: 1.2;
      text-transform: uppercase;
    }

    strong {
      min-width: 0;
      color: #2d3b4c;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.35;
      word-break: break-word;
    }
  }

  .call-contacts__mobile-list {
    display: none;
  }

  .call-contacts__pagination {
    display: flex;
    justify-content: flex-end;
  }

  .call-contacts__empty {
    margin: 0;
    padding: 18px 12px;
    color: var(--cc-faint);
    text-align: center;
    border: 1px dashed var(--cc-border-strong);
    border-radius: 8px;
    background: var(--cc-surface-soft);
  }

  .call-contacts-slide-down-enter-active,
  .call-contacts-slide-down-leave-active {
    transition:
      opacity 0.22s ease,
      transform 0.22s ease,
      max-height 0.28s ease;
    transform-origin: top center;
    overflow: hidden;
  }

  .call-contacts-slide-down-enter,
  .call-contacts-slide-down-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-10px) scaleY(0.98);
  }

  .call-contacts-slide-down-enter-to,
  .call-contacts-slide-down-leave {
    max-height: 1400px;
    opacity: 1;
    transform: translateY(0) scaleY(1);
  }

  @media (width <=768px) {
    min-height: calc(100vh - 56px);
    padding: 0;
    background: #ffffff;

    .call-contacts__panel {
      min-height: calc(100vh - 56px);
      padding: 12px;
    }

    .call-contacts__head,
    .call-contacts__start,
    .call-contacts__phone,
    .call-contacts__assigned,
    .call-contacts__form {
      border-radius: 8px;
      box-shadow: none;
    }

    .call-contacts__head,
    .call-contacts__start,
    .call-contacts__section-head,
    .call-contacts__filters {
      align-items: stretch;
      flex-direction: column;
      gap: 10px;
    }

    .call-contacts__form,
    .call-contacts__result-form {
      grid-template-columns: 1fr;
    }

    .call-contacts__role-group,
    .call-contacts__role-group .el-radio-button,
    .call-contacts__role-group .el-radio-button__inner {
      width: 100%;
    }

    .call-contacts__financial-fields {
      grid-template-columns: 1fr;
    }

    .call-contacts__form .el-button,
    .call-contacts__start .el-button,
    .call-contacts__filters .el-date-editor,
    .call-contacts__filters--overview .el-select,
    .call-contacts__filters--overview .el-date-editor,
    .call-contacts__submit-button {
      width: 100%;
      min-width: 0;
    }

    .call-contacts__overview {
      padding: 14px;
      border-radius: 8px;
      box-shadow: none;
    }

    .call-contacts__kpi-grid,
    .call-contacts__overview-grid {
      grid-template-columns: 1fr;
    }

    .call-contacts__kpi-card {
      padding: 12px;

      strong {
        font-size: 24px;
      }
    }

    .call-contacts__chart-panel,
    .call-contacts__managers-panel {
      padding: 12px;
      box-shadow: none;
    }

    .call-contacts__chart {
      overflow-x: auto;

      svg,
      .call-contacts__chart-labels {
        min-width: 560px;
      }
    }

    .call-contacts__overview-table {
      display: none;
    }

    .call-contacts__overview-mobile {
      display: grid;
    }

    .call-contacts__queue-empty {
      align-items: flex-start;
    }

    .call-contacts__phone {
      padding: 34px 14px 14px;
    }

    .call-contacts__phone--inline {
      margin-top: 0;
      padding: 14px;
      box-shadow: none;
    }

    .call-contacts__phone--mobile-inline {
      border-style: solid;
      background: #ffffff;
    }

    .call-contacts__phone-head {
      flex-direction: column;
      gap: 10px;
      padding-right: 0;
    }

    .call-contacts__collapse-button {
      top: 10px;
      right: 14px;
      height: 24px;
      min-height: 24px;
      padding: 0 8px;
      color: var(--cc-blue);
      background: transparent;
      border-color: transparent;
      box-shadow: none;
    }

    .call-contacts__collapse-label {
      display: inline;
    }

    .call-contacts__number {
      font-size: 24px;
    }

    .call-contacts__meta-grid {
      grid-template-columns: 1fr;

      div {
        border-right: 0;
        border-bottom: 1px solid var(--cc-border);
      }

      div:last-child {
        border-bottom: 0;
      }
    }

    .call-contacts__result-form {
      gap: 0;

      .el-radio {
        padding: 7px 0;
      }
    }

    .call-contacts__radio-group {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px 12px;
      width: 100%;
    }

    .call-contacts__result-form .el-form-item:nth-child(3) .call-contacts__radio-group {
      grid-template-columns: 1fr;
    }

    .call-contacts__submit-button {
      justify-self: stretch;
      margin-top: 2px;
    }

    .call-contacts__table {
      display: none;
    }

    .call-contacts__mobile-list {
      display: grid;
      gap: 10px;
      min-height: 72px;
    }

    .call-contacts__mobile-card {
      display: grid;
      gap: 14px;
      padding: 14px;
      border: 1px solid var(--cc-border);
      border-radius: 8px;
      background:
        linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%),
        var(--cc-surface);
    }

    .call-contacts__mobile-card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
    }

    .call-contacts__mobile-phone {
      min-width: 0;
      font-size: 18px;
      font-weight: 800;
      line-height: 1.25;
      word-break: break-word;
    }

    .call-contacts__assigned-info--mobile {
      gap: 8px;
      padding: 10px;
      border: 1px solid #e7edf4;
      border-radius: 8px;
      background: #f8fafc;
    }

    .call-contacts__assigned-info--mobile.call-contacts__assigned-info--result .call-contacts__assigned-info-item {
      padding: 0;
      background: transparent;
      border: 0;
      border-radius: 0;
    }

    .call-contacts__assigned-info-item {
      grid-template-columns: 1fr;
      gap: 3px;

      span {
        font-size: 10px;
      }

      strong {
        font-size: 14px;
        line-height: 1.45;
      }
    }

    .call-contacts__mobile-meta {
      display: grid;
      gap: 8px;
      margin: 0;

      div {
        display: grid;
        gap: 2px;
      }

      dt {
        color: var(--cc-faint);
        font-size: 12px;
        font-weight: 700;
        line-height: 1.2;
      }

      dd {
        min-width: 0;
        margin: 0;
        color: #344253;
        font-size: 14px;
        line-height: 1.35;
        word-break: break-word;
      }
    }

    .call-contacts__mobile-action {
      width: 100%;
      margin-left: 0;
    }

    .call-contacts__pagination {
      justify-content: center;
      overflow-x: auto;
      padding-bottom: 2px;
    }

    .call-contacts__manager-metrics {
      grid-template-columns: 1fr;
    }
  }

  @media (width <=380px) {
    .call-contacts__panel {
      padding: 10px;
    }

    .call-contacts__mobile-card-head {
      flex-direction: column;
      align-items: flex-start;
    }

    .call-contacts__radio-group {
      grid-template-columns: 1fr;
    }

    .call-contacts__kpi-card strong {
      font-size: 22px;
    }
  }

  @media (width <=480px) {
    .call-contacts__assigned-info--mobile .call-contacts__assigned-info-item {
      span {
        font-size: 9px;
      }

      strong {
        font-size: 12px;
      }
    }
  }
}
</style>
